import { ChevronDownIcon } from 'lucide-react';
import { cn } from '@/core/lib/utils';

interface ConvertToTaskPickerTriggerProps extends React.ComponentProps<'button'> {
  /** Selected value's label. Absent shows the placeholder in muted type. */
  label?: string | null;
  placeholder: string;
  icon?: React.ReactNode;
  /** Blue outline the design gives every AI-filled control. */
  highlighted?: boolean;
}

/**
 * Makes a picker that brings its own popover look like the dialog's selects.
 *
 * `StatusPicker` and `GroupPicker` are the task detail's, where they sit as bare inline
 * text in a metadata row. Reused inside this form they need the same 40px bordered box as
 * every other field, so the trigger is passed in rather than the component forked.
 *
 * Props are spread onto the button, ref included, because Radix's `PopoverTrigger asChild`
 * clones this element to attach its own handlers and ref.
 */
export function ConvertToTaskPickerTrigger({
  label,
  placeholder,
  icon,
  highlighted,
  className,
  ...props
}: ConvertToTaskPickerTriggerProps) {
  return (
    <button
      type="button"
      className={cn(
        'flex h-10 w-full items-center gap-2 rounded-md border border-input bg-transparent px-3 text-sm shadow-xs',
        'transition-[color,box-shadow] outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50',
        'disabled:cursor-not-allowed disabled:opacity-50 dark:bg-input/30 dark:hover:bg-input/50',
        highlighted && 'border-primary',
        className,
      )}
      {...props}
    >
      {icon}
      <span className={cn('min-w-0 flex-1 truncate text-left', !label && 'text-muted-foreground')}>
        {label || placeholder}
      </span>
      <ChevronDownIcon className="size-4 shrink-0 opacity-50" aria-hidden />
    </button>
  );
}
